// Minimal RLP encoder, just enough for EIP-1559 (type 0x02) transactions.
// We deliberately reimplement instead of pulling in a dep because:
//   - The iframe's dependency tree is part of its trust surface; smaller is
//     safer.
//   - The full @ethereumjs/rlp package adds ~5 KB to a bundle we want under
//     30 KB. We only need to encode, not decode, and only fixed shapes.
//
// RLP spec (Ethereum Yellow Paper, Appendix B):
//   - For a single byte 0x00..0x7f, encoding is the byte itself.
//   - For a 0..55-byte string, encoding is [0x80 + len, ...bytes].
//   - For a >55-byte string, encoding is
//       [0xb7 + lenOfLen, ...bigEndianLen, ...bytes].
//   - For a 0..55-byte list (concatenation of encodings), encoding is
//       [0xc0 + len, ...items].
//   - For a >55-byte list, encoding is
//       [0xf7 + lenOfLen, ...bigEndianLen, ...items].

export type RlpInput = Uint8Array | RlpInput[];

export function rlpEncode(input: RlpInput): Uint8Array {
  if (input instanceof Uint8Array) return encodeBytes(input);
  if (!Array.isArray(input)) throw new Error('rlp: input must be bytes or array');
  const body = concat(input.map(rlpEncode));
  return concat([encodeLength(body.length, 0xc0), body]);
}

function encodeBytes(bytes: Uint8Array): Uint8Array {
  if (bytes.length === 1 && bytes[0] < 0x80) {
    return bytes;
  }
  return concat([encodeLength(bytes.length, 0x80), bytes]);
}

function encodeLength(len: number, offset: number): Uint8Array {
  if (len < 56) {
    return new Uint8Array([offset + len]);
  }
  const hex = len.toString(16);
  const padded = hex.length % 2 === 0 ? hex : '0' + hex;
  const lenBytes = hexToBytesRaw(padded);
  const out = new Uint8Array(1 + lenBytes.length);
  out[0] = offset + 55 + lenBytes.length;
  out.set(lenBytes, 1);
  return out;
}

function concat(arrays: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const a of arrays) total += a.length;
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrays) {
    out.set(a, off);
    off += a.length;
  }
  return out;
}

function hexToBytesRaw(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) hex = '0' + hex;
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

// hexToUnpaddedBytes parses a 0x-prefixed hex string and strips leading
// zeros. RLP requires that integer fields be "minimal-length" — leading
// zeros are illegal. So `0x00` encodes as the empty byte string `0x80`,
// not `0x00 0x80`.
export function hexToUnpaddedBytes(hex: string): Uint8Array {
  if (!hex.startsWith('0x')) throw new Error('rlp: expected 0x-prefixed hex');
  let s = hex.slice(2);
  while (s.length > 1 && s.startsWith('0')) s = s.slice(1);
  if (s === '0') return new Uint8Array(0);
  return hexToBytesRaw(s);
}

// hexToBytesExact parses a 0x-prefixed hex string into the exact bytes
// it represents. Used for fields that are addresses/data (where leading
// zeros are meaningful), as opposed to integers.
export function hexToBytesExact(hex: string): Uint8Array {
  if (!hex.startsWith('0x')) throw new Error('rlp: expected 0x-prefixed hex');
  return hexToBytesRaw(hex.slice(2));
}
